/* eslint-disable no-magic-numbers,no-plusplus */


/**
 * Helper utilities, generally for managing and manipulating selections in the Editor.
 *
 * 		checkForTab(event, editor)
 * 		classToNumber(value)
 * 		cleanAfterInput(theNode, whitelistBR = true, restoreSelection = false)
 * 		cleanSelection(theParentNode, whitelistBR)
 * 		cleanupPaste(settings)
 * 		clearSelection(node)
 * 		convertClasslistToObject(classes)
 * 		editorIsEmpty(event, editor)
 * 		featuresClassName(otFeatures, stylisticSets)
 * 		findClosestParagraph(node)
 * 		fontClassName(family, style)
 * 		fontDetailsFromClassName(className)
 * 		fontHasLigatures(features)
 * 		formatBlock(thePrefix, theClass)
 * 		formatSelection(thePrefix, className, reuseContainer)
 * 		getClassByNamespace(namespace, classList)
 * 		getClosest(elem, nodeName)
 * 		getRequiredClassesFromParentEls(childElement)
 * 		insertAfter(newNode, referenceNode)
 * 		insertTextIfEmpty(editorIsFocused, theText)
 * 		isMetaKeyOnly(keyCode)
 * 		isParagraphEmpty(p)
 * 		isTextWrappedInNode(theSelection)
 * 		logger({ color = 'blue', enabled = true, label, lines, padding = [0, 1] })
 * 		mergeSiblings(theParentNode)
 * 		moveCaretToEndOfNode(range, selection, node)
 * 		moveCaretToStartOfNode(el)
 * 		normalizeSelection(initialSetupDone)
 * 		numberToClass(value, decimalReplacer = formatting.decimalReplacer)
 * 		replaceNamespacedClass(el, thePrefix, className, recursive = false)
 * 		selectAllText(el)
 * 		selectedTextHasContainer(sel)
 * 		selectedTextMatchesNodeText()
 * 		setLayoutType({ props, mobileSource })
 * 		theCSSValue(stringFormat)
 * 		unpackSelection(theParentNode)
 * 		wrapSelectionWithClass(className)
 */

// Dependencies
import rangy from 'rangy/lib/rangy-classapplier';
import 'rangy/lib/rangy-selectionsaverestore';
import 'rangy/lib/rangy-textrange';

// Config
import {
	formatting,
	layoutBreakpoints as LayoutBreakpoints,
	nodeTypes as NodeTypes,
	prefix,
	toolbarOptions
} from 'pages/Overview/config';


// Lodash
import {
	chain,
	concat,
	debounce,
	filter,
	find,
	findIndex,
	flatten,
	forEach,
	get,
	indexOf,
	isEmpty,
	join,
	map,
	pull,
	trim,
	uniq,
	values
} from 'lodash';


// Global config for utils
const CONFIG = {
	keyCodes: {
		backspace: 8,
		tab: 9,
		v: 86
	}
};


/**
 * Set the layout type based on the data passed in.
 * This sits outside of the React component because it is needed in several
 * places throughout the app, one of which is `getDerivedStateFromProps` where
 * you can’t use `this`. So we just pass in the data sources that hold the info
 * we need and return the appropriate string.
 *
 * @method setLayoutType
 * @param  {Object}      props         Always this.props from within the component.
 * @param  {Object}      mobileSource  Source data from varying sources.
 */
export function setLayoutType({ props, mobileSource }) {
	// Set the mobile data source to `props` if `mobileSource` isn’t set.
	const path = mobileSource || props;

	const isMobile = !props.breakpoints[LayoutBreakpoints.desktop];
	const hasMobileLayout = get(path, 'layouts.mobile.html', false);
	let activeLayoutType = isMobile ? 'mobile' : 'desktop';
	activeLayoutType = hasMobileLayout ? activeLayoutType : 'desktop';

	return activeLayoutType;
}


/**
 * Check a font’s OpenType features array to see if it supports ligatures.
 *
 * @param  {Array} features
 */
export function fontHasLigatures(features) {
	const hasLigatures = find(features, feature => feature.code === 'liga');
	return !!hasLigatures || false;
}


/**
 * Check for a keyDown of the TAB key. If detected, replace with spaces.
 *
 * @method checkForTab
 * @param  {Event}    event  onKeyDown event from editor
 * @param  {Node}     props  editor DOM node
 * @return {Void}
 */
export function checkForTab(event, editor) {
	if (event.keyCode === CONFIG.keyCodes.tab) {
		const container = editor.current;
		event.preventDefault();

		// now insert four non-breaking spaces for the tab key
		const doc = container.ownerDocument.defaultView;
		const sel = doc.getSelection();
		const range = sel.getRangeAt(0);

		const tabNode = document.createTextNode('\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0');
		range.insertNode(tabNode);

		range.setStartAfter(tabNode);
		range.setEndAfter(tabNode);
		sel.removeAllRanges();
		sel.addRange(range);
	}
}

/**
 * Gather classes from every parent element
 *
 * @param  {Node} childElement The DOM node to start with
 * @return {Array}
 */
export function getRequiredClassesFromParentEls(childElement) {
	const theFeatures = values(prefix);
	const theClasses = [];
	let theElement = childElement;

	// Get the prefix from the supplied className and check theFeatures array
	// to see if we’ve yet accounted for it
	const checkClassName = className => {
		if (typeof className === 'string') {
			const theClass = className.split('__');
			const thePrefix = `${theClass[0]}__`;
			if (theFeatures.indexOf(thePrefix) > -1) {
				pull(theFeatures, thePrefix);
				theClasses.push(className);
			}
		}
	};

	for (; theElement && theElement !== document; theElement = theElement.parentNode) {
		const elClasslist = values(theElement.classList);
		elClasslist.forEach(checkClassName);

		// Return the complete classList when we reach a <p/> tag or we’ve run
		// out of features in theFeatures array to check for.
		if (theFeatures.length === 0 || theElement.nodeName.toLowerCase() === 'p') { return theClasses; }
	}

	return false;
}


/**
 * Get a specific className from a classList based on the namespace
 * prefix passed in.
 *
 * @param  {string} namespace Namespace to search the classList for. Will look
 *                            something like `font__`. See /src/config.js for
 *                            full list.
 * @param  {object} classList Array-like object of classes on an element.
 * @return {string|bool}      Return either the className or false.
 */
export function getClassByNamespace(namespace, classList) {
	const classIndex = findIndex(classList, object => object.indexOf(namespace) > -1);
	if (classIndex > -1) { return classList[classIndex]; }
	return false;
}


/**
 * Check to see if the key(s) pressed are meta keys only.
 * When you press only a metaKey no change is required to the editor
 * content, so we exit out.
 * @return {bool}
 */
export function isMetaKeyOnly(keyCode) {
	if (
		keyCode === 91 ||
		(keyCode > 15 && keyCode < 19)
	) { return true; }
	return false;
}


/**
 * Check to see if the Editor is empty.
 * This is primarily used to keep the browser from removing the last
 * remaining paragraph when the user selects all content and hits “delete”
 * multiple times.
 *
 * @return {bool}
 */
export function editorIsEmpty(event, editor) {
	const editorChildNodes = get(editor, 'childNodes');

	if (
		!editorChildNodes ||
		(
			editorChildNodes.length === 1 &&
			editorChildNodes[0].textContent === '' &&
			event.keyCode === CONFIG.keyCodes.backspace
		)
	) {
		event.preventDefault();
		return true;
	}

	return false;
}


/**
 * Move a caret to the end of a node
 *
 * @param  {object} range     The Rangy range we're working with
 * @param  {object} selection The Rangy selection object
 * @param  {object} node      The node we'll append the caret to
 */
export function moveCaretToEndOfNode(range, selection, node) {
	if (node.parentNode) {
		range.setStartAfter(node);
		range.setEndAfter(node);
		selection.removeAllRanges();
		selection.addRange(range);
	}
}


/**
 * Get closest DOM element up the tree that contains a class, ID, or data attribute
 *
 * @param  {Node} elem The base element
 * @param  {String} selector The class, id, data attribute, or tag to look for
 * @return {Node} Null if no match
 */
export function getClosest(elem, nodeName) {
	let theElement = elem;

	if (!theElement) { return false; }

	// Return if element passed in matches the nodeName
	if (theElement.nodeName && theElement.nodeName.toLowerCase() === nodeName) { return theElement; }

	// Get closest match
	for (; theElement && theElement !== document; theElement = theElement.parentNode) {
		if (theElement.nodeName && theElement.nodeName.toLowerCase() === nodeName) { return theElement; }
	}

	return false;
}


export const normalizeParent = debounce(settings => {
	if (!settings.initialSetupDone) { return false; }

	// Get current selection
	const selection = rangy.getSelection();
	const range = selection.getRangeAt(0);

	// Get <p/> nodes contained in selection.
	const pNodes = range.getNodes([1], node =>
		node.nodeName.toLowerCase() === 'p'
	);

	// If no <p/> nodes, get parent <p/>.
	if (pNodes.length === 0) {
		let theParentNode = selection.anchorNode.parentElement;
		if (theParentNode.nodeName.toLowerCase() !== 'p') {
			theParentNode = getClosest(theParentNode, 'p');
		}
		cleanAfterInput(theParentNode);
	// else loop through all <p/> nodes in selection.
	} else {
		pNodes.forEach(node => cleanAfterInput(node));
	}

	return true;
}, 200);


export function cleanAfterInput(theNode, whitelistBR = true, restoreSelection = false) {
	let currentSelection;
	if (restoreSelection) { currentSelection = rangy.saveSelection(); }
	try {
		theNode.removeAttribute('style');
	} catch (err) { /* nothing */ }
	unpackSelection(theNode);
	cleanSelection(theNode, whitelistBR);
	mergeSiblings(theNode);
	if (restoreSelection) { rangy.restoreSelection(currentSelection); }
}


/**
 * Un-nest child nodes that are at the beginning or end of a parent node.
 * When a span is at the beginning or end of a node, move it up one level to make
 * it the previous or next sibling (respecively) of the parent node.
 *
 * @param  {object} theParentNode
 *
 * Function is recursive.
 */
export function unpackSelection(theParentNode) {
	if (!theParentNode) { return; }
	let children = theParentNode.childNodes;
	let numberOfChildren = children ? children.length : 0;

	for (let index = 0; index < numberOfChildren; index++) {
		const thisChild = children[index];
		if (thisChild && thisChild.outerHTML !== undefined) {
			unpackSelection(thisChild);
			children = theParentNode.childNodes;
		}
	}

	children = theParentNode.childNodes;
	numberOfChildren = children ? children.length : 0;

	// If the parent node is a span, we need to rearrange its children
	// before processing the node. This ensures that any child nodes that are
	// at the start or the end of the parent node get properly moved out and processed.
	if (theParentNode.nodeName && theParentNode.nodeName.toLowerCase() === 'span') {
		// Pull any child spans before the current span if they have no text before them
		while (theParentNode.firstChild && theParentNode.firstChild.nodeName.toLowerCase() === 'span') {
			try {
				theParentNode.firstChild.removeAttribute('style');
			} catch (err) { /* nothing */ }
			const myFirstChild = theParentNode.firstChild;
			// If the parent has a class the child does not, apply that class to the child before
			// moving it out of the span.
			for (let idx = 0; idx < theParentNode.classList.length; idx++) {
				if (!theParentNode.firstChild.classList.contains(theParentNode.classList[idx])) {
					const classPrefix = `${theParentNode.classList[idx].split('__')[0]}__`;
					let containsAlternateClass = false;
					for (let zdx = 0; zdx < theParentNode.firstChild.classList.length; zdx++) {
						const myClassPrefix = `${theParentNode.firstChild.classList[zdx].split('__')[0]}__`;
						if (myClassPrefix === classPrefix) { containsAlternateClass = true; break; }
					}
					if (!containsAlternateClass) { myFirstChild.classList.add(theParentNode.classList[idx]); }
				}
			}
			theParentNode.parentNode.insertBefore(myFirstChild, theParentNode);
		}

		// Pull any child spans after the current span if they have no text after them
		while (theParentNode.lastChild && theParentNode.lastChild.nodeName.toLowerCase() === 'span') {
			try {
				theParentNode.lastChild.removeAttribute('style');
			} catch (err) { /* nothing */ }
			const myLastChild = theParentNode.lastChild;
			// If the parent has a class the child does not, apply that class to the child before
			// moving it out of the span.
			for (let idx = 0; idx < theParentNode.classList.length; idx++) {
				if (theParentNode.lastChild.classList.contains(theParentNode.classList[idx]) === -1) {
					const classPrefix = `${theParentNode.classList[idx].split('__')[0]}__`;
					let containsAlternateClass = false;
					for (let zdx = 0; zdx < theParentNode.lastChild.classList.length; zdx++) {
						const myClassPrefix = `${theParentNode.lastChild.classList[zdx].split('__')[0]}__`;
						if (myClassPrefix === classPrefix) { containsAlternateClass = true; break; }
					}
					if (!containsAlternateClass) { myLastChild.classList.add(theParentNode.classList[idx]); }
				}
			}
			insertAfter(myLastChild, theParentNode);
		}

		// Check to see if the child we removed spans from has any remaining children.
		// If not, this span can be removed before processing.
		if (!theParentNode.childNodes || theParentNode.childNodes.length === 0) {
			theParentNode.remove();
		}

		// At this point, theParentNode.children should contain 0 or more child nodes, but the first and last
		// child nodes should not be of type 'span'
	}
}


/**
 * Clean the parent paragraph.
 * @param  {object} theParentNode
 * Normalizes nodes contained in paragraph, moving redundant classes up as far
 * as possible in the DOM.
 *
 * Function is recursive and will go as deep as possible to find redundant classes
 * on child spans and move them to the highest-possible parent (the parent with no
 * other content than spans containing the same class).
 */
export function cleanSelection(theParentNode, whitelistBR) {
	if (!theParentNode) { return; }
	let children = filter(theParentNode.childNodes, (item) => item.nodeType === 1);
	const numberOfChildren = children ? children.length : 0;
	const childClasses = {};
	let childHTMLAsString = '';
	let spanCount = 0;

	for (let index = 0; index < numberOfChildren; index++) {
		if (children[index].outerHTML !== undefined) {
			cleanSelection(children[index]);
		}
	}

	// Create an array of classnames that are shared between all child classes
	// with counts for each. This is so we can compare their counts to the
	// number of children
	for (let index = 0; index < numberOfChildren; index++) {
		if (children[index].nodeName.toLowerCase() === 'span') {
			try {
				children[index].removeAttribute('style');
			} catch (err) { /* nothing */ }
			childHTMLAsString += children[index].outerHTML.trim();
			const childClassCount = children[index].classList.length;
			for (let zed = 0; zed < childClassCount; zed++) {
				// If the class hasnâ€™t been added to the `childClasses` array,
				// add it and set it to 1â€¦
				if (childClasses[children[index].classList[zed]] === undefined) {
					childClasses[children[index].classList[zed]] = 1;
				} else {
					// Otherwise just increment.
					childClasses[children[index].classList[zed]]++;
				}
			}
			spanCount++;
		}
	}

	if (childHTMLAsString.trim() === (theParentNode.innerHTML && theParentNode.innerHTML.trim())) {
		forEach(childClasses, (child, key) => {
			if (child === spanCount) {
				// You've found a class that exists in all children.
				// Remove it from the children:
				let numChildren = numberOfChildren;
				for (let index = 0; index < numChildren; index++) {
					if (children[index].nodeName.toLowerCase() === 'span') {
						try {
							children[index].removeAttribute('style');
						} catch (err) { /* nothing */ }
						children[index].classList.remove(key);
						// Remove empty spans
						if (children[index].classList.length === 0) {
							while (children[index].firstChild) {
								theParentNode.insertBefore(children[index].firstChild, children[index]);
							}
							theParentNode.removeChild(children[index]);

							// After removing a child, you have to shorten your loop. You also need
							// to reset the 'children' array because the nodes use a new pointer
							// when you remove a child. It's annoying.
							index--;
							numChildren--;
							children = filter(theParentNode.childNodes, (item) => item.nodeType === 1);
						}
					}
				}

				// Remove the analogous class from the parent
				const classPrefix = `${key.split('__')[0]}__`;
				for (let zed = 0; zed < theParentNode.classList.length; zed++) {
					if (theParentNode.classList[zed].substring(0, classPrefix.length) === classPrefix) {
						theParentNode.classList.remove(theParentNode.classList[zed]);
					}
				}
				// Add the new class to the parent
				theParentNode.classList.add(key);
			}
		});
		return;
	}

	// If the parent doesn't contain only span children then individually look
	// at each child. If the child's classes are a subset of the parent's and
	// don't contain any additional classes, the span can be removed from around the child.
	for (let index = 0; index < numberOfChildren; index++) {
		let okToRemove = true;
		for (let zndex = 0; zndex < children[index].classList.length; zndex++) {
			if (!theParentNode.classList.contains(children[index].classList[zndex])) {
				okToRemove = false;
			}
		}
		if (okToRemove) {
			while (children[index].firstChild) {
				theParentNode.insertBefore(children[index].firstChild, children[index]);
			}
			if (children[index].nodeName.toLowerCase() === 'span' || (whitelistBR === false && children[index].nodeName.toLowerCase() === 'br')) {
				theParentNode.removeChild(children[index]);
			}
		}
	}
}


/**
 * Merge identical siblings in child nodes.
 * @param  {object} theParentNode
 * Normalizes nodes contained in paragraph, merging sibling nodes with identical class
 * lists together
 *
 * Function is recursive.
 */
export function mergeSiblings(theParentNode) {
	if (!theParentNode) { return; }
	let children = theParentNode.childNodes;
	let numberOfChildren = children ? children.length : 0;

	for (let index = 0; index < numberOfChildren; index++) {
		if (children[index + 1] && children[index + 1].nodeName.toLowerCase() === 'span' && children[index].nodeName.toLowerCase() === 'span') {
			if (children[index].classList.length === children[index + 1].classList.length) {
				let allSame = true;

				// Sort the nodeLists of this node
				const classListThisNodeArray = [];
				for (let yndex = 0; yndex < children[index].classList.length; yndex++) {
					classListThisNodeArray[classListThisNodeArray.length] = children[index].classList[yndex];
				}
				const sortedClassesThisNode = classListThisNodeArray.sort();

				// Sort the nodeLists of the next node
				const classListNextNodeArray = [];
				for (let yndex = 0; yndex < children[index + 1].classList.length; yndex++) {
					classListNextNodeArray[classListNextNodeArray.length] = children[index + 1].classList[yndex];
				}
				const sortedClassesSiblingNode = classListNextNodeArray.sort();

				// Loop through and compare classes between nodes.
				for (let yndex = 0; yndex < children[index].classList.length; yndex++) {
					if (sortedClassesThisNode[yndex] !== sortedClassesSiblingNode[yndex]) {
						allSame = false;
						break;
					}
				}

				// If no different classes were found in the sorted lists they can be merged together.
				if (allSame) {
					// Move all the children out of the next sibling and put them into the current node
					for (let yndex = 0; yndex < children[index + 1].childNodes.length; yndex++) {
						children[index].appendChild(children[index + 1].childNodes[yndex]);
					}

					// Remove the next sibling
					children[index + 1].remove();

					// Reset the children array now that it has new children to account for
					children = theParentNode.childNodes;
					numberOfChildren = children ? children.length : 0;

					// Reset the counter back one to reprocess this node now that it (potentially) has a new sibling.
					index--;
				}
			}
		}
	}

	// After we've merged all the children at this level, process all the merged node's children.
	children = filter(theParentNode.childNodes, (item) => item.nodeType === 1);
	numberOfChildren = children ? children.length : 0;
	if (numberOfChildren > 1) {
		for (let index = 0; index < numberOfChildren; index++) {
			mergeSiblings(children[index]);
		}
	}
}


export function insertAfter(newNode, referenceNode) {
	referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
}


export function cleanupPaste(settings) {
	if (settings.isMobile) { return false; }

	const allParagraphs = this.Editor.childNodes;
	const className = fontClassName(settings.selectedFamily.label, settings.selectedStyle.id);

	forEach(allParagraphs, node => {
		if (node) {
			const childNodes = node.childNodes;
			if (childNodes.length === 1 && childNodes[0].tagName === 'BR') {
				node.parentNode.removeChild(node);
			}

			const nodeClasslist = node.classList;
			if (isEmpty(nodeClasslist) && node.classList) {
				node.classList.add(className, `size__${settings.size.value}`);
			}
		}
	});

	return true;
}


/**
 * Get the font family and style from className
 *
 * @param  {str} className The formatted CSS className with family and style
 * @return {arr}           Returns an array with the family being at [1] and
 *                         the style being at [2]
 */
export function fontDetailsFromClassName(className) {
	return className.match('.*__(.*)--(.*)');
}


/**
 * Create a CSS class based on the font family and style selected
 *
 * @param  {string} family Font family
 * @param  {string} style  Font style
 * @return {string}        Formatted string for use as CSS class
 */
export function fontClassName(family, style) {
	if (family === null || family === undefined || style === null) { return false; }

	let theFamily = family;
	theFamily = theFamily.replace(/&\s/g, '');
	theFamily = theFamily.replace(/ /g, '-');
	return `${prefix.font}${theFamily}--${style}`;
}


/**
 * Convert string value from a CSS class to numeric value
 *
 * @param  {String} value
 * @return {Number}
 */
export function classToNumber(value) {
	return parseFloat(value.replace(/-/, '.'), 10);
}


/**
 * Convert numeric value to string for use in classes
 *
 * @param  {Number} value
 * @return {String}
 */
export function numberToClass(value, decimalReplacer = formatting.decimalReplacer) {
	return value.toString().replace(/\./g, decimalReplacer);
}


/**
 * Convert whole-number value to decimal value for use in CSS
 *
 * @param  {number} wholeNumberValue The whole number to convert.
 * @return {number}
 */
export function theCSSValue(stringFormat) {
	const cssValue = Number(stringFormat.toString().replace(formatting.decimalReplacer, '.'));
	if (isNaN(cssValue)) { return false; }
	return cssValue;
}


/**
 * Apply class to paragraphs
 *
 * @param  {string} prefix    The CSS class prefix we’re working with.
 * @param  {string} className The new class to apply.
 */
export function formatBlock(thePrefix, theClass) {
	// Get Scribe selection
	const selection = new global.scribe.api.Selection();

	// Check to see if multiple p tags are selected
	const sel = rangy.getSelection();
	const range = sel.getRangeAt(0);
	const pNodes = range.getNodes([1], (node) =>
		node.nodeName.toLowerCase() === 'p'
	);

	// If caret is currently *inside* of a <p> element
	if (!pNodes.length) {
		const parentNode = selection.getContaining(node =>
			global.scribe.node.isBlockElement(node) && global.scribe.el.contains(node)
		);

		// If a parentNode is found
		if (parentNode) {
			const previousNamespacedClass = getClassByNamespace(prefix[thePrefix], parentNode.classList);
			if (previousNamespacedClass) { parentNode.classList.remove(previousNamespacedClass); }
			parentNode.classList.add(theClass);
		}
	} else {
		pNodes.forEach((node) => {
			const previousNamespacedClass = getClassByNamespace(prefix[thePrefix], node.classList);
			if (previousNamespacedClass) { node.classList.remove(previousNamespacedClass); }
			node.classList.add(theClass);
		});
	}
}


/**
 * Cleanup function to be run after all toolbar actions.
 */
export function normalizeSelection(initialSetupDone) {
	if (!initialSetupDone) { return false; }

	// Get current selection
	const selection = rangy.getSelection();
	const range = selection.getRangeAt(0);

	// Get <p/> nodes contained in selection.
	const pNodes = range.getNodes([1], (node) =>
		node.nodeName.toLowerCase() === 'p'
	);

	// I no <p/> nodes, get parent <p/>.
	if (pNodes.length === 0) {
		let theParentNode = selection.anchorNode.parentElement;

		if (theParentNode) {
			if (theParentNode.nodeName.toLowerCase() === 'div') {
				theParentNode = selection.anchorNode;
			} else if (theParentNode.nodeName.toLowerCase() === 'span') {
				theParentNode = getClosest(theParentNode, 'p');
			}
			cleanAfterInput(theParentNode, true, true);
		}
	// else loop through all <p/> nodes in selection.
	} else {
		pNodes.forEach(node => cleanAfterInput(node, true, true));
	}

	return true;
}


/**
 * Replace a namespaced class on an element
 *
 * @param  {object} el        The node to modify.
 * @param  {string} prefix    The namespace to search for and replace.
 * @param  {string} className The class to apply.
 * @return {object}           Return the updated classList.
 */
export function replaceNamespacedClass(el, thePrefix, className, recursive = false) {
	const spanNodes = filter(el.childNodes, item =>
		item.nodeType === 1 && item.nodeName.toLowerCase() === 'span'
	);

	if (spanNodes.length && recursive === true) {
		spanNodes.forEach((span) => {
			replaceNamespacedClass(span, thePrefix, className, true);
		});
	}

	const previousFontClass = getClassByNamespace(thePrefix, el.classList);

	if (previousFontClass) { el.classList.remove(previousFontClass); }

	el.classList.add(className);

	return el.classList;
}


/**
 * Run checks on selected text to see how formatting should be handled.
 *
 * @param  {string} prefix    The CSS class prefix we’re working with.
 * @param  {string} className The new class to apply.
 */
export function formatSelection(thePrefix, className, reuseContainer) {
	if (typeof className !== 'string') { return false; }

	// Check to see if the selected text is already wrapped in
	// an element we can style.
	const elementWrapper = reuseContainer ? false : selectedTextHasContainer();

	// Check to see if the user has selected multiple paragraphs
	// and their entire contents.
	const canUseParagraphNodes = selectedTextMatchesNodeText();

	// Store reference to the element that gets create (in the event that one
	// needs to be created).
	let theElementCreated = true;

	// 1. Visitor has selected one ore more paragraphs and their entire contents;
	if (canUseParagraphNodes) {
		canUseParagraphNodes.forEach(node => {
			replaceNamespacedClass(node, prefix[thePrefix], className);

			// Check paragraph for <span /> tags and update classNames
			// accordingly.
			const spanNodes = filter(node.childNodes, (item) => item.nodeType === 1 && item.nodeName.toLowerCase() === 'span');
			if (spanNodes.length) {
				spanNodes.forEach(span => {
					replaceNamespacedClass(span, prefix[thePrefix], className, true);
				});
			}
		});
	// 2. Visitor has selected text that is already wrapped in a reusable el; or
	} else if (elementWrapper) {
		replaceNamespacedClass(elementWrapper, prefix[thePrefix], className);
	// 3. Catch-all. Just wrap the selected text in a span and apply the class.
	} else {
		theElementCreated = wrapSelectionWithClass(className);
	}

	return theElementCreated;
}


/**
 * Select text has reusable wrapper node.
 */
export function selectedTextHasContainer(sel) {
	const selection = sel || rangy.getSelection();
	if (selection.rangeCount === 0) { return false; }

	const range = selection.getRangeAt(0);

	let parentElement = range.commonAncestorContainer;
	const parentElementText = trim(parentElement.nodeValue);
	if (parentElement.nodeType === 3) {
		if (parentElement.parentNode.nodeName.toLowerCase() !== 'div') { parentElement = parentElement.parentNode; }
	}

	return trim(range.toString()) === parentElementText ? parentElement : false;
}


/**
 * Selected text matches node text.
 * Take the text from the selection and determine if it matches the text from
 * the parent (or contained) paragraphs.
 *
 * Use case: Visitor has selected all text on the screen, or all text within one
 * or more paragraphs.
 */
export function selectedTextMatchesNodeText() {
	/**
	 * Cycle through the selected elements and see if the text
	 * selected represents the entire contents of the parent
	 * elements.
	 */
	const selection = rangy.getSelection();

	// Should we just break out here if there are no ranges detected?
	if (selection.rangeCount === 0) { return false; }

	const range = selection.getRangeAt(0);
	const pNodes = range.getNodes([1], (node) =>
		node.nodeName.toLowerCase() === 'p' || node.nodeName.toLowerCase() === 'span'
	);

	// Should we break out here if there are no paragraphs detected?
	if (!pNodes.length) { return false; }

	// Cycle through child nodes to gather textContent.
	let textContent = '';
	pNodes.forEach((node) => {
		if (node.textContent !== '') {
			textContent += node.textContent;
		}
	});

	if (textContent.trim() === selection.toString().trim()) { return pNodes; }
	return false;
}


/**
 * Find the closest acceptable node (p|span) in the selection, including the
 * node immediately surrounding the selected text. This was the solution to the
 * problem where selecting text and then changing the font would show the
 * *parent* node’s font setting in the controls instead of the selected
 * texts’ node.
 *
 * @method isTextWrappedInNode
 * @param  {Selection}         theSelection the currently-selected text
 * @return {Boolean}
 */
export function isTextWrappedInNode(theSelection) {
	const selection = theSelection || rangy.getSelection();
	let parentElement = null;

	if (selection.rangeCount > 0) {
		const range = selection.getRangeAt(0);
		const selectedText = range.text();
		parentElement = range.commonAncestorContainer;

		// If the nodeType is text, use the parentElement
		if (parentElement.nodeType === NodeTypes.text) {
			parentElement = parentElement.parentNode;
		}

		// Get the childNode, if it exists
		const childNode = get(selection, 'focusNode.firstElementChild', null);

		// Get the childNode’s text content
		const childTextContent = get(childNode, 'textContent', null);

		// Get the current parent’s text content
		const parentTextContent = parentElement.textContent;

		// If the currently-selected text content matched the text content of the childNode…
		if (selectedText === childTextContent) {
			return childNode;
		// If the currently-selected text content matches the text content of the parentNode…
		} else if (selectedText === parentTextContent) {
			return parentElement;
		// Select the parentNode if the other two options are false
		} else { // Catch-all (selection is likely collapsed)
			return parentElement;
		}
	}

	return null;
}


/**
 * Wrap the selected text in a <span/> with a class.
 * @param  {string} className
 */
export function wrapSelectionWithClass(className) {
	// Create the CSS class and inline styles based on the font
	// parameters passed in from the JSON
	let theElementCreated;
	const theRangyClass = rangy.createClassApplier(className, {
		applyToEditableOnly: true,
		onElementCreate: el => { theElementCreated = el; },
		tagNames: ['p', 'span'],
		useExistingElements: true
	});

	// Apply the class and styles to the selected text
	theRangyClass.applyToSelection();

	return theElementCreated;
}


/**
 * Generate the otFeatures and stylisticSets and package into a className for
 * updating the selected/focused style.
 *
 * @method featuresClassName
 * @param  {Array}          otFeatures
 * @param  {Array}          stylisticSets
 * @return {Object}
 */
export function featuresClassName(otFeatures, stylisticSets) {
	if (otFeatures === null || stylisticSets === null) { return false; }

	/** Set up Stylistic Sets *************************************************/

	// All supported OpenType features, from the master config file
	const allStylisticSets = toolbarOptions.stylisticSets;
	// Find and format all features from the master feature list that aren’t
	// currently selected by the user.
	// `map` returns an array with the values
	// `pull` removes all undefined
	const disabledStylisticSets = pull(map(allStylisticSets, (item) => { // eslint-disable-line consistent-return
		if (indexOf(stylisticSets.value, item.value) === -1) { return `"${item.value}" 0`; }
	}), undefined);

	// And format all enabled features
	const enabledStylisticSets = map(stylisticSets.value, (item) => {
		if (item === '') { return ''; }
		return `"${item}" 1`;
	});

	/** Set up OpenType Features **********************************************/

	// All supported OpenType features, from the master config file
	const allOpenTypeFeatures = toolbarOptions.openTypeFeatures;

	// Find and format all features from the master feature list that aren’t
	// currently selected by the user.
	// `map` returns an array with the values
	// `pull` removes all undefined
	const disabledOpenTypeFeatures = pull(map(allOpenTypeFeatures, (item) => { // eslint-disable-line consistent-return
		if (indexOf(otFeatures.value, item.value) === -1) { return `"${item.value}" 0`; }
	}), undefined);

	// And format all enabled features
	const enabledOpenTypeFeatures = map(otFeatures.value, (item) => {
		if (item === '') { return ''; }
		return `"${item}" 1`;
	});

	// Now combine both `disabledOpenTypeFeatures` and `enabledOpenTypeFeatures` into
	// a single array
	const masterFeatureList = concat(disabledOpenTypeFeatures, enabledOpenTypeFeatures, disabledStylisticSets, enabledStylisticSets);

	return {
		features: masterFeatureList,
		className: `${prefix.features}${join(otFeatures.value, '-')}-${join(stylisticSets.value, '-')}`.replace(/-\s*$/, '')
	};
}


/**
 * Insert Text If Empty
 * Checks the current selection. If it’s collapsed (no text selected) and
 * the only child of a paragraph, we’ll inset the supplied text into the
 * paragraph itself. If there is other text in the paragraph, it inserts it
 * as a simple text node.
 *
 * @param  {string} theText The text to insert.
 * @return {object}
 */
export function insertTextIfEmpty(editorIsFocused, theText) {
	// If no text passed in, stop!
	if (!editorIsFocused || isEmpty(theText)) { return false; }

	const selection = rangy.getSelection();

	if (selection.isCollapsed) {
		const range = selection.getRangeAt(0);
		const theNode = selection.anchorNode;
		const currentNodeIsParagraph = theNode.nodeName.toLowerCase() === 'p';

		// Create and insert the text
		let nodeToSelect;
		if (currentNodeIsParagraph) {
			theNode.textContent = theText;
			nodeToSelect = theNode;
		} else {
			const textNode = document.createTextNode(theText);
			range.insertNode(textNode);
			nodeToSelect = textNode;
		}

		// Select the contents of the tag created/used
		const newRange = document.createRange();
		newRange.selectNodeContents(nodeToSelect);
		selection.addRange(newRange);

		return newRange;
	}
	return false;
}


/**
 * Convert classList to object with keys:values
 *
 * Takes an array of classLists like this:
 *
 *		[
 *			["font__Peristyle--1775", "tracking__0", "alignment__center", "features__pnum", "size__180", "leading__90"],
 *			["tracking__0", "alignment__center", "features__pnum", "leading__90", "font__Ringside--1638", "size__20"]
 *		]
 *
 * … removes duplicates and converts it to key:value pairs:
 *
 *		{
 *			alignment: "center",
 *			features: "pnum",
 *			font: false,
 *			leading: 90,
 *			size: false,
 *			tracking: 0
 *		}
 *
 * @param  {array} classes An array of all classes in the selection
 * @return {object}        Object containing each namespace from the classes.
 *                         Each namespace should return either `false` (which
 *                         means it contains multiple instances with different
 *                         values), or a string with the value.
 */
export function convertClasslistToObject(classes) {
	// Flatten and sort the array, the remove duplicates
	const flattenedClassList = flatten(classes);
	const sortedClassList = flattenedClassList.sort();
	const theClasses = uniq(sortedClassList);


	// Get the keys to collect from the config prefix object
	const theKeysToGather = values(prefix);

	const theResults = {};

	// Iterate over theKeysToGather and search theClasses for matching values.
	theKeysToGather.forEach(key => {
		const theValues = theClasses.filter(item => item.indexOf(key) > -1);

		if (theValues.length === 0) { return false; }

		const propAndValue = theValues.length ? theValues[0].split('__') : null;

		// First check for font classes
		if (propAndValue[0] === 'font') {
			// Search the classes and 1. find the font classes; 2. get only the
			// family name; 3. boil this array down to unique values.
			// If we’re left with an array.length of 1, this means that all font
			// classes are for the same family and the family select menu can
			// remain enabled.
			const fontClasses =
				chain(theClasses)
					.filter(item => item.startsWith(prefix.font))				// get only classes that start with `font__`
					.map(item => {
						const fontData = fontDetailsFromClassName(item);		// split the class into family and style
						return {
							family: fontData[1],
							style: parseInt(fontData[2], 10)
						};
					})
					.value();

			const fonts = chain(fontClasses).map(item => item.family).uniq().value(); // boil just the famlies from the array and include only unique values
			const style = chain(fontClasses).map(item => item.style).uniq().value(); // boil just the styles from the array and include only unique values

			theResults.fonts = fonts;
			theResults.styles = style;
		// If several values are present and we aren’t working on “features”…
		} else if (theValues.length > 1 && propAndValue[0] !== 'features') {
			theResults[propAndValue[0]] = false;
		// If several values are present and we ARE working on “features”…
		} else if (theValues.length > 1 && propAndValue[0] === 'features') {
			theResults[propAndValue[0]] = '';

			// Cycle through and collect all feature values
			theValues.forEach(theValue => {
				const valueOnly = theValue.replace(prefix.features, '');
				theResults[propAndValue[0]] = theResults[propAndValue[0]] + (theResults[propAndValue[0]] !== '' ? '-' : '') + valueOnly;
			});
		} else if (theValues.length) {
			const theValueAsNumber = parseInt(propAndValue[1], 10);
			let theValue = propAndValue[1];

			if (theValue.includes('x')) {
				theValue = theValue.replace('x', '.');
			} else if (!isNaN(theValueAsNumber)) {
				// theValue = theValue;
				// No need to reassign here, but leaving as a reference as to
				// what number values require. This condition covers classes
				// with values that are strings.
			} else {
				theValue = propAndValue[1];
			}

			theResults[propAndValue[0]] = theValue;
		}

		return true;
	});

	return theResults;
}

/**
 * Remove any selections in the node that’s passed in.
 *
 * @method clearSelection
 * @param  {Node}       node The node we want to remove selection from
 * @return {Void}
 */
export function clearSelection(node) {
	const selection = rangy.getSelection(node);
	selection.removeAllRanges();
}


/**
 * Filter paragraph for anyting but <br /> tags. If nothing is returned,
 * paragraph is considered empty. Searches only one level deep. No recursion.
 *
 * @method isParagraphEmpty
 * @param  {Node}         p  Paragraph passed in fron Editor content
 * @return {Boolean}         Is the paragraph empty?
 */
export function isParagraphEmpty(p) {
	if (p.nodeName.toLowerCase() !== 'p') { return false; }

	const childNodes = p.childNodes;
	const children = filter(childNodes, item =>
		item.nodeName.toLowerCase() !== 'br'
	);

	if (children.length > 0) { return false; }

	return true;
}


/**
 * Select all text inside of the given node.
 *
 * @method selectAllText
 * @param  {Node}      el  The element that should have all text selected
 * @return {Void}
 */
export function selectAllText(el) {
	const win = window;
	const doc = win.document;
	let sel;
	let range;

	if (win.getSelection && doc.createRange) {
		sel = win.getSelection();
		range = doc.createRange();
		range.selectNodeContents(el);
		sel.removeAllRanges();
		sel.addRange(range);
	} else if (doc.body.createTextRange) {
		range = doc.body.createTextRange();
		range.moveToElementText(el);
		range.select();
	}
}


export function findClosestParagraph(node) {
	const type = node.nodeType;
	const isParagraph = node.nodeName.toLowerCase() === 'p';
	const whitelistNodeTypes = ['p', '#text'];
	let pNode;

	if (whitelistNodeTypes.indexOf(node.nodeName) < 0) { return null; }

	if (isParagraph) { return node; }

	switch (type) {
		case nodeTypes.element: {
			pNode = node.nodeParent.closest('p');
			break;
		}
		case nodeTypes.text: {
			const nodeParent = node.parentElement;
			pNode = nodeParent.closest('p');
			break;
		}
		default:
			pNode = node.parentElement;
	}

	return pNode;
}

export function moveCaretToStartOfNode(el) {
	const range = document.createRange();
	const sel = window.getSelection();
	range.setStart(el, 0);
	range.collapse(true);
	sel.removeAllRanges();
	sel.addRange(range);
}


/* eslint-disable no-console */
export function logger({ color = 'blue', enabled = true, label, lines, padding = [0, 1] }) {
	if (!enabled) { return false; }
	console.group(`%c${label}`, `font-weight: bold; color: ${color}; font-family: "Operator Mono SSm"; font-size: 12px;`);
	for (let i = 0; i < padding[0]; i++) { console.log('\n'); }
	lines.map(line => console.log(`	${line.label}:`, line.data));
	for (let i = 0; i < padding[1]; i++) { console.log('\n'); }
	console.groupEnd();

	return true;
}
/* eslint-enable no-console */
